import React, { useEffect, useState } from "react";
import TopNav from './TopNav';
import FooterSection from './FooterSection';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ENDPOINTS } from "../configs/endpoints";
import CircularProgress from "@mui/material/CircularProgress";

const CheckIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        className="flex-shrink-0"
    >
        <path
            d="M10 0C4.49 0 0 4.49 0 10C0 15.51 4.49 20 10 20C15.51 20 20 15.51 20 10C20 4.49 15.51 0 10 0ZM14.78 7.7L9.11 13.37C8.97 13.51 8.78 13.59 8.58 13.59C8.38 13.59 8.19 13.51 8.05 13.37L5.22 10.54C4.93 10.25 4.93 9.77 5.22 9.48C5.51 9.19 5.99 9.19 6.28 9.48L8.58 11.78L13.72 6.64C14.01 6.35 14.49 6.35 14.78 6.64C15.07 6.93 15.07 7.4 14.78 7.7Z"
            fill="url(#paint0_linear_tx)"
        />
        <defs>
            <linearGradient id="paint0_linear_tx" x1="20" y1="20" x2="0" y2="0" gradientUnits="userSpaceOnUse">
                <stop stopColor="white" />
                <stop offset="1" stopColor="#EED59E" />
            </linearGradient>
        </defs>
    </svg>
)

const ChevronIcon = ({ isExpanded }: { isExpanded: boolean }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}
    >
        <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
)

// Helper function to convert camelCase to Title Case
const formatLabel = (key: string): string => {
    return key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
        .trim()
}

// Helper function to format values
const formatValue = (value: any): string => {
    if (typeof value === "boolean") return value ? "Yes" : "No"
    if (value === null || value === undefined) return "N/A"
    return String(value)
}

// Helper function to truncate long values (like addresses or hashes)
const truncateValue = (value: string, maxLength = 60): string => {
    if (value.length <= maxLength) return value
    return `${value.substring(0, maxLength)}...`
}

const ProductDetail = () => {
    const [activeTab, setActiveTab] = useState(1);
    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const serial = decodeURIComponent(params.get("serial"));
    const code = decodeURIComponent(params.get("code"));
    const [productDetails, setProductDetails] = useState(null);
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})
    const [logs, setLogs] = useState([])
    const [events, setEvents] = useState([])
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        console.log("Serial Number:", serial);
        getBlockchainDetails(serial);
    }, [serial]);

    const getBlockchainDetails = async (serialNumber: string | null) => {
        if (!serialNumber) {
            return;
        };
        try {
            const response = await fetch(`${ENDPOINTS.PUBLIC_BLOCKCHAIN_DETAILS}/${encodeURIComponent(code)}/${encodeURIComponent(serialNumber)}`);
            const data = await response.json();
            setProductDetails({ ...data, itemDetails: JSON.parse(data?.itemDetails), blockChainResponse: JSON.parse(data?.blockChainResponse) });
            setLogs(JSON.parse(data?.blockChainResponse)?.logs || []);
            setEvents(JSON.parse(data?.blockChainResponse)?.events || []);
            console.log("Blockchain Details:", data);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching blockchain details:", error);
        }
    };

    // Group events by type
    const groupedEvents: Record<string, any[]> = {}
    events.forEach((event) => {
        const eventType = event.type || "unknown"
        if (!groupedEvents[eventType]) {
            groupedEvents[eventType] = []
        }
        groupedEvents[eventType].push(event)
    })

    const toggleSection = (sectionKey: string) => {
        setExpandedSections((prev) => ({
            ...prev,
            [sectionKey]: !prev[sectionKey],
        }))
    }

    const tabs = [
        {
            id: 1, name: "Product Details", icon: (
                <svg className="w-[25px] h-[25px]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" fill="none">
                    <path d="M5 13V13.01M5 9L5 9.01M5 5V5.01M13 13V13.01M13 9V9.01M13 5V5.01M9 5V5.01M9 13V13.01M3 1L15 1C16.1046 1 17 1.89543 17 3V15C17 16.1046 16.1046 17 15 17H3C1.89543 17 1 16.1046 1 15L1 3C1 1.89543 1.89543 1 3 1Z" stroke={`${activeTab === 1 ? "black" : "#9E9E9E"}`} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            )
        },
        {
            id: 2, name: "Transaction History", icon: (
                <svg className="w-[25px] h-[25px]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
                    <path d="M12.8 6.99997C12.6188 6.68574 12.3557 6.42668 12.0386 6.25053C11.7215 6.07438 11.3625 5.98777 11 5.99997L9 5.99997C8.46957 5.99997 7.96086 6.21069 7.58579 6.58576C7.21071 6.96083 7 7.46954 7 7.99997C7 8.53041 7.21071 9.03911 7.58579 9.41419C7.96086 9.78926 8.46957 9.99997 9 9.99997H11C11.5304 9.99997 12.0391 10.2107 12.4142 10.5858C12.7893 10.9608 13 11.4695 13 12C13 12.5304 12.7893 13.0391 12.4142 13.4142C12.0391 13.7893 11.5304 14 11 14H9C8.63749 14.0122 8.27849 13.9256 7.96142 13.7494C7.64435 13.5733 7.38115 13.3142 7.2 13M10 4V6M10 14V16M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke={`${activeTab === 2 ? "black" : "#9E9E9E"}`} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            )
        },
        {
            id: 3, name: "Certifications", icon: (
                <svg className="w-[25px] h-[25px]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 19" fill="none">
                    <path d="M11 13.5L11 18L13 16.5L15 18L15 13.5M8 15H3C2.46957 15 1.96086 14.7893 1.58579 14.4142C1.21071 14.0391 1 13.5304 1 13L1 3C1 1.9 1.9 1 3 1L17 1C17.5304 1 18.0391 1.21071 18.4142 1.58579C18.7893 1.96086 19 2.46957 19 3V13C18.9996 13.3507 18.9071 13.6952 18.7315 13.9988C18.556 14.3025 18.3037 14.5546 18 14.73M4 5L16 5M4 8H7M4 11H6M16 11C16 12.6569 14.6569 14 13 14C11.3431 14 10 12.6569 10 11C10 9.34315 11.3431 8 13 8C14.6569 8 16 9.34315 16 11Z" stroke={`${activeTab === 3 ? "black" : "#9E9E9E"}`} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            )
        },
        {
            id: 4, name: "Seller Information", icon: (
                <svg className="w-[25px] h-[25px]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" fill="none">
                    <path d="M9 5H9.01M8 9H9V13H10M3 1L15 1C16.1046 1 17 1.89543 17 3V15C17 16.1046 16.1046 17 15 17H3C1.89543 17 1 16.1046 1 15L1 3C1 1.89543 1.89543 1 3 1Z" stroke={`${activeTab === 4 ? "black" : "#9E9E9E"}`} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            )
        },
    ];

    return (

        <div className="min-h-screen bg-[#130d01]">
            <TopNav />
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-loader-circle-icon lucide-loader-circle"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
            <section className="h-screen max-w-[1700px] m-auto m-0 pt-[160px] pb-[60px] xl:pt-[120px] xl:pb-[80px]" id="product-detail">

                {loading && (
                    <div className="inner-wrap max-w-[1700px] m-auto m-0 xl:pt-[0] xl:pb-[0] pl-[10px] pr-[10px] lg:pl-[25px] lg:pr-[25px] xl:pl-[50px] xl:pr-[50px]">
                    <div className="slide-holder-1 flex-row xl:flex align-center justify-center p-[5px] border-solid border border-[rgb(214,129,0)] rounded-xl mb-[50px]  lg:mt-[80px] w-full lg:w-4/6 items-center mx-auto">
                        <span className="bg-[#] flex flex-row gap-6 items-center justify-center font-regular text-[#C68804] text-[16px] sm:text-[16px] md:text-[16px] pt-[15px] pb-[10px] pr-[20px] pl-[20px] w-full block text-center">
                            <svg focusable="false" width="50" height="50" viewBox="0 0 24 24" aria-hidden="true" role="img"><path d="M0 0h24v24H0V0z" fill="none"></path><path fill="#F3DAA3" d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"></path></svg>
                            <p className="pt-[5px] text-[16px] sm:text-[16px] md:text-[16px] text-left text-[#F3DAA3]">
                                We couldn’t find your item in our system. Please double-check the details provided. If the issue continues, share your partner code and serial number with us at concierge@elystrion.com so we can assist you promptly.
                            </p>
                        </span>
                    </div>
                    </div>
                )}

                {!loading && (
                    <div className="inner-wrap max-w-[1700px] m-auto m-0 xl:pt-[0] xl:pb-[0] pl-[10px] pr-[10px] lg:pl-[25px] lg:pr-[25px] xl:pl-[50px] xl:pr-[50px]">
                        <div className="slide-holder flex flex-col xl:flex-row w-full min-h-[250px] xl:min-h-[500px] overflow-hidden slide-holder-1 align-center justify-between bg-[rgba(0,0,0,1)] lg:mt-[80px] rounded-[25px]">
                            <div className="xl:w-[50%] h-[250px] xl:h-[500px] flex order-2 xl:order-1 items-center left text-[#ffffff] text-[16px] sm:text-[16px] md:text-[16px] p-[50px]">
                                <span className="font-regular text-[#CACACA] text-[52px] sm:text-[62x] md:text-[72px] leading-tight mb-[20px] w-full">{productDetails?.itemCatelogName}</span>
                            </div>
                            <div className="xl:w-[50%] h-[250px] xl:h-[500px] flex right order-1 xl:order-2 bg-[#ffffff] text-[#ffffff] text-[16px] sm:text-[16px] md:text-[16px] p-[50px]">
                            </div>
                        </div>

                        <div className="product-copy mt-[80px]">
                            <ul className="flex flex-row gap-10 border-b-2 border-[#F3DAA3] justify-between lg:justify-normal">
                                {tabs.map((tab) => (
                                    <li key={tab.id}>
                                        <span
                                            onClick={() => setActiveTab(tab.id)}
                                            className={`flex h-full flex-col lg:flex-row text-center gap-4 p-[20px] items-center justify-center rounded-t-[10px] cursor-pointer transition-all duration-500 ease-in-out ${activeTab === tab.id ? "bg-[#F3DAA3] text-black" : "bg-transparent text-white"
                                                }`}
                                        >
                                            {tab.icon}
                                            <p className="text-[16px] sm:text-[16px] md:text-[18px] transition-colors duration-500 ease-in-out hidden md:block">{tab.name}</p>
                                        </span>
                                    </li>
                                ))}
                                <li>
                                    <p></p>
                                </li>
                            </ul>

                            <div className="para mt-[50px]">
                                {activeTab === 1 && (
                                    <div className={`${activeTab === 1 ? 'block animate-fadeInUp' : 'hidden'}`}>

                                        <p className=" font-semi-bold text-[#CACACA] text-[28px] sm:text-[32px] md:text-[32px] leading-tight mb-[30px] mt-[20px] block md:hidden">Product Details</p>

                                        <p className="text-[#CACACA] text-[18px] sm:text-[18x] md:text-[22px] leading-10">
                                            {productDetails?.itemDescription}
                                        </p>

                                        {productDetails?.itemDetails && Object.entries(productDetails?.itemDetails).map(([sectionKey, sectionValue]) => {
                                            const items = Array.isArray(sectionValue) ? sectionValue : [sectionValue];
                                            return (
                                                <div className="mt-[50px]">
                                                    <p className=" font-medium text-[#CACACA] text-[20px] sm:text-[24px] md:text-[28px] leading-tight mb-[20px]">{formatLabel(sectionKey)}</p>

                                                    {items.map((item, itemIndex) => {
                                                        const showItemNumber = items.length > 1;

                                                        return (
                                                            <ul className="flex flex-col gap-5 mt-[30px]">
                                                                {Object.entries(item).map(([key, value]) => (
                                                                    <li key={key}>
                                                                        <span className="flex flex-row gap-4 bg-transparent items-start justify-start">
                                                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                                                <path d="M10 0C4.49 0 0 4.49 0 10C0 15.51 4.49 20 10 20C15.51 20 20 15.51 20 10C20 4.49 15.51 0 10 0ZM14.78 7.7L9.11 13.37C8.97 13.51 8.78 13.59 8.58 13.59C8.38 13.59 8.19 13.51 8.05 13.37L5.22 10.54C4.93 10.25 4.93 9.77 5.22 9.48C5.51 9.19 5.99 9.19 6.28 9.48L8.58 11.78L13.72 6.64C14.01 6.35 14.49 6.35 14.78 6.64C15.07 6.93 15.07 7.4 14.78 7.7Z" fill="url(#paint0_linear_202_797)" />
                                                                                <defs>
                                                                                    <linearGradient id="paint0_linear_202_797" x1="20" y1="20" x2="0" y2="0" gradientUnits="userSpaceOnUse">
                                                                                        <stop stop-color="white" />
                                                                                        <stop offset="1" stop-color="#EED59E" />
                                                                                    </linearGradient>
                                                                                </defs>
                                                                            </svg>
                                                                            <p className="text-[#ffffff] text-[16px] sm:text-[16px] md:text-[18px]">
                                                                                <span className="text-[#9e9e9e] font-bold">{formatLabel(key)}:</span> {formatValue(value)}
                                                                            </p>
                                                                        </span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        );
                                                    })}

                                                </div>
                                            );
                                        })}

                                    </div>
                                )}

                                {activeTab === 2 && (
                                    <div className={`${activeTab === 2 ? 'block animate-fadeInUp' : 'hidden'}`}>
                                        <p className=" font-semi-bold text-[#CACACA] text-[28px] sm:text-[32px] md:text-[32px] leading-tight mb-[30px] mt-[20px] block md:hidden">Certifications</p>
                                        <p className="text-[#CACACA] text-[18px] sm:text-[18x] md:text-[22px] leading-10">
                                            This stunning ring eatures a brilliant round-cut diamond as its centerpiece, securely set in lustrous white gold and accented with pavé-set diamonds along the band for added sparkle. Each diamond is expertly selected for dazzling clarity and fire, with superior craftsmanship ensuring lasting beauty and durability. The classic four-prong setting showcases the main stone’s brilliance, making this ring a perfect expression of timeless elegance and refined luxury. Ideal for celebrating life’s most cherished moments, its sophisticated design will captivate admirers and serve as a lasting symbol of devotion.
                                        </p>

                                        {logs.length > 0 && (
                                            <div className="mt-[50px]">
                                                <button
                                                    onClick={() => toggleSection("logs")}
                                                    className="w-full flex items-center justify-between font-medium text-[#CACACA] text-[20px] sm:text-[24px] md:text-[28px] leading-tight mb-[20px] hover:text-[#EED59E] transition-colors cursor-pointer"
                                                >
                                                    <span>Transaction Logs</span>
                                                    <ChevronIcon isExpanded={expandedSections["logs"] ?? true} />
                                                </button>

                                                <div
                                                    className={`overflow-hidden transition-all duration-300 ${(expandedSections["logs"] ?? true) ? "max-h-[5000px] opacity-100" : "max-h-0 opacity-0"
                                                        }`}
                                                >
                                                    <ul className="flex flex-col gap-5">
                                                        {logs.map((log, index) => (
                                                            <li key={index}>
                                                                <span className="flex flex-row gap-4 bg-transparent items-start justify-start">
                                                                    <CheckIcon />
                                                                    <p className="text-[#ffffff] text-[16px] sm:text-[16px] md:text-[18px]">
                                                                        <span className="text-[#9e9e9e] font-bold">Log #{index + 1}:</span> {formatValue(log)}
                                                                    </p>
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        )}

                                        {Object.entries(groupedEvents).map(([eventType, eventList]) => {
                                            const isExpanded = expandedSections[eventType] ?? true

                                            return (
                                                <div key={eventType} className="mt-[50px]">
                                                    <button
                                                        onClick={() => toggleSection(eventType)}
                                                        className="w-full flex items-center justify-between font-medium text-[#CACACA] text-[20px] sm:text-[24px] md:text-[28px] leading-tight mb-[20px] hover:text-[#EED59E] transition-colors cursor-pointer bg-transparent border-0"
                                                    >
                                                        <span>{formatLabel(eventType)} Events</span>
                                                        <ChevronIcon isExpanded={isExpanded} />
                                                    </button>

                                                    <div
                                                        className={`overflow-hidden transition-all duration-300 ${isExpanded ? "max-h-[5000px] opacity-100" : "max-h-0 opacity-0"
                                                            }`}
                                                    >
                                                        {eventList.map((event, eventIndex) => {
                                                            const showEventNumber = eventList.length > 1
                                                            const attributes = event.attributes || []

                                                            return (
                                                                <div key={eventIndex} className="mb-[30px]">
                                                                    {showEventNumber && (
                                                                        <p className="text-[#9e9e9e] text-[16px] font-semibold mb-[15px]">Event #{eventIndex + 1}</p>
                                                                    )}

                                                                    <ul className="flex flex-col gap-5">
                                                                        {attributes.map((attr: any, attrIndex: number) => {
                                                                            const key = attr.key || `attribute_${attrIndex}`
                                                                            const value = attr.value || "N/A"
                                                                            const displayValue = truncateValue(formatValue(value))

                                                                            return (
                                                                                <li key={attrIndex}>
                                                                                    <span className="flex flex-row gap-4 bg-transparent items-start justify-start">
                                                                                        <CheckIcon />
                                                                                        <p className="text-[#ffffff] text-[16px] sm:text-[16px] md:text-[18px] break-all">
                                                                                            <span className="text-[#9e9e9e] font-bold">{formatLabel(key)}:</span> {displayValue}
                                                                                        </p>
                                                                                    </span>
                                                                                </li>
                                                                            )
                                                                        })}
                                                                    </ul>
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                </div>
                                            )
                                        })}

                                        {logs.length === 0 && events.length === 0 && (
                                            <div className="mt-[50px]">
                                                <p className="text-[#9e9e9e] text-[18px]">No transaction data available.</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeTab === 3 && (
                                    <div className={`${activeTab === 3 ? 'block animate-fadeInUp' : 'hidden'}`}>
                                        <p className=" font-semi-bold text-[#CACACA] text-[28px] sm:text-[32px] md:text-[32px] leading-tight mb-[30px] mt-[20px] block md:hidden">Transaction History</p>
                                        <p className="text-[#CACACA] text-[18px] sm:text-[18x] md:text-[22px] leading-10">
                                            This stunning ring eatures a brilliant round-cut diamond as its centerpiece, securely set in lustrous white gold and accented with pavé-set diamonds along the band for added sparkle. Each diamond is expertly selected for dazzling clarity and fire, with superior craftsmanship ensuring lasting beauty and durability. The classic four-prong setting showcases the main stone’s brilliance, making this ring a perfect expression of timeless elegance and refined luxury. Ideal for celebrating life’s most cherished moments, its sophisticated design will captivate admirers and serve as a lasting symbol of devotion.
                                        </p>
                                        <div className="mt-[50px]">
                                            <p className=" font-medium text-[#CACACA] text-[20px] sm:text-[24px] md:text-[28px] leading-tight mb-[20px]">Stone type: Natural Sapphire</p>
                                            <ul className="flex flex-col gap-5 mt-[30px]">
                                                <li>
                                                    <span className="flex flex-row gap-4 bg-[transparent] items-left justify-left">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                            <path d="M10 0C4.49 0 0 4.49 0 10C0 15.51 4.49 20 10 20C15.51 20 20 15.51 20 10C20 4.49 15.51 0 10 0ZM14.78 7.7L9.11 13.37C8.97 13.51 8.78 13.59 8.58 13.59C8.38 13.59 8.19 13.51 8.05 13.37L5.22 10.54C4.93 10.25 4.93 9.77 5.22 9.48C5.51 9.19 5.99 9.19 6.28 9.48L8.58 11.78L13.72 6.64C14.01 6.35 14.49 6.35 14.78 6.64C15.07 6.93 15.07 7.4 14.78 7.7Z" fill="url(#paint0_linear_202_797)" />
                                                            <defs>
                                                                <linearGradient id="paint0_linear_202_797" x1="20" y1="20" x2="0" y2="0" gradientUnits="userSpaceOnUse">
                                                                    <stop stop-color="white" />
                                                                    <stop offset="1" stop-color="#EED59E" />
                                                                </linearGradient>
                                                            </defs>
                                                        </svg><p className="text-[#ffffff] text-[16px] sm:text-[16px] md:text-[18px]">Mineral Family: Beryl (Be₃Al₂(SiO₃)₆)</p>
                                                    </span>
                                                </li>
                                                <li>
                                                    <span className="flex flex-row gap-4 bg-[transparent] items-left justify-left">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                            <path d="M10 0C4.49 0 0 4.49 0 10C0 15.51 4.49 20 10 20C15.51 20 20 15.51 20 10C20 4.49 15.51 0 10 0ZM14.78 7.7L9.11 13.37C8.97 13.51 8.78 13.59 8.58 13.59C8.38 13.59 8.19 13.51 8.05 13.37L5.22 10.54C4.93 10.25 4.93 9.77 5.22 9.48C5.51 9.19 5.99 9.19 6.28 9.48L8.58 11.78L13.72 6.64C14.01 6.35 14.49 6.35 14.78 6.64C15.07 6.93 15.07 7.4 14.78 7.7Z" fill="url(#paint0_linear_202_797)" />
                                                            <defs>
                                                                <linearGradient id="paint0_linear_202_797" x1="20" y1="20" x2="0" y2="0" gradientUnits="userSpaceOnUse">
                                                                    <stop stop-color="white" />
                                                                    <stop offset="1" stop-color="#EED59E" />
                                                                </linearGradient>
                                                            </defs>
                                                        </svg><p className="text-[#ffffff] text-[16px] sm:text-[16px] md:text-[18px]">Crystal System: Hexagonal</p>
                                                    </span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                )}

                                {activeTab === 4 && (
                                    <div className={`${activeTab === 4 ? 'block animate-fadeInUp' : 'hidden'}`}>
                                        <p className=" font-semi-bold text-[#CACACA] text-[28px] sm:text-[32px] md:text-[32px] leading-tight mb-[30px] mt-[20px] block md:hidden">Seller Information</p>
                                        <p className="text-[#CACACA] text-[18px] sm:text-[18x] md:text-[22px] leading-10">
                                            This stunning ring eatures a brilliant round-cut diamond as its centerpiece, securely set in lustrous white gold and accented with pavé-set diamonds along the band for added sparkle. Each diamond is expertly selected for dazzling clarity and fire, with superior craftsmanship ensuring lasting beauty and durability. The classic four-prong setting showcases the main stone’s brilliance, making this ring a perfect expression of timeless elegance and refined luxury. Ideal for celebrating life’s most cherished moments, its sophisticated design will captivate admirers and serve as a lasting symbol of devotion.
                                        </p>
                                        <div className="mt-[50px]">
                                            <p className=" font-medium text-[#CACACA] text-[20px] sm:text-[24px] md:text-[28px] leading-tight mb-[20px]">Stone type: Natural Sapphire</p>
                                            <ul className="flex flex-col gap-5 mt-[30px]">
                                                <li>
                                                    <span className="flex flex-row gap-4 bg-[transparent] items-left justify-left">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                            <path d="M10 0C4.49 0 0 4.49 0 10C0 15.51 4.49 20 10 20C15.51 20 20 15.51 20 10C20 4.49 15.51 0 10 0ZM14.78 7.7L9.11 13.37C8.97 13.51 8.78 13.59 8.58 13.59C8.38 13.59 8.19 13.51 8.05 13.37L5.22 10.54C4.93 10.25 4.93 9.77 5.22 9.48C5.51 9.19 5.99 9.19 6.28 9.48L8.58 11.78L13.72 6.64C14.01 6.35 14.49 6.35 14.78 6.64C15.07 6.93 15.07 7.4 14.78 7.7Z" fill="url(#paint0_linear_202_797)" />
                                                            <defs>
                                                                <linearGradient id="paint0_linear_202_797" x1="20" y1="20" x2="0" y2="0" gradientUnits="userSpaceOnUse">
                                                                    <stop stop-color="white" />
                                                                    <stop offset="1" stop-color="#EED59E" />
                                                                </linearGradient>
                                                            </defs>
                                                        </svg><p className="text-[#ffffff] text-[16px] sm:text-[16px] md:text-[18px]">Mineral Family: Beryl (Be₃Al₂(SiO₃)₆)</p>
                                                    </span>
                                                </li>
                                                <li>
                                                    <span className="flex flex-row gap-4 bg-[transparent] items-left justify-left">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                            <path d="M10 0C4.49 0 0 4.49 0 10C0 15.51 4.49 20 10 20C15.51 20 20 15.51 20 10C20 4.49 15.51 0 10 0ZM14.78 7.7L9.11 13.37C8.97 13.51 8.78 13.59 8.58 13.59C8.38 13.59 8.19 13.51 8.05 13.37L5.22 10.54C4.93 10.25 4.93 9.77 5.22 9.48C5.51 9.19 5.99 9.19 6.28 9.48L8.58 11.78L13.72 6.64C14.01 6.35 14.49 6.35 14.78 6.64C15.07 6.93 15.07 7.4 14.78 7.7Z" fill="url(#paint0_linear_202_797)" />
                                                            <defs>
                                                                <linearGradient id="paint0_linear_202_797" x1="20" y1="20" x2="0" y2="0" gradientUnits="userSpaceOnUse">
                                                                    <stop stop-color="white" />
                                                                    <stop offset="1" stop-color="#EED59E" />
                                                                </linearGradient>
                                                            </defs>
                                                        </svg><p className="text-[#ffffff] text-[16px] sm:text-[16px] md:text-[18px]">Crystal System: Hexagonal</p>
                                                    </span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                )}

            </section>

            <FooterSection />
        </div>
    );
};

export default ProductDetail;